import { useMemo } from 'react';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useCioBrowseFacets from './useCioBrowseFacets';
import useCioBrowseProducts from './useCioBrowseProducts';

// IMPORTANT: this hook is used like a Context hook so make sure to memoize all objects/arrays
const useCioBrowseAndGqlProducts = () => {
  const { selectedUrlFilters, allCioFacets, allCioFacetNames } =
    useCioBrowseFacets();

  const {
    data: cioBrowseResults,
    // isLoading,
    isFetching: isCioBrowseFetching,
    // error,
    fetchNextPage,
    hasNextPage,
  } = useCioBrowseProducts({ selectedUrlFilters, allCioFacets });

  const masterProductIds = useMemo(() => {
    return cioBrowseResults?.pages
      ?.flatMap((page) => page?.response?.results)
      ?.map((result) => result?.data.id);
  }, [cioBrowseResults]);

  // TODO handle redirects and prevent further fetching

  const { data: pricingData } = useBatchedGqlProductPricing(masterProductIds);
  const { data: relatedProductsData, isLoading: isRelatedProductsLoading } =
    useBatchedGqlRelatedProducts(masterProductIds);

  // Combine the cioBrowseResults with the pricingData and relatedProductsData
  const products = useMemo(
    () =>
      cioBrowseResults?.pages
        ?.flatMap((page) => page.response?.results)
        ?.map((result) => ({
          ...result?.data,
          pricing: pricingData?.find(
            (pricing) =>
              Number(pricing.masterProductId) === Number(result?.data.id)
          ),
          relatedProductsData: relatedProductsData?.find(
            (relatedProducts) =>
              Number(relatedProducts.masterProductId) ===
              Number(result?.data.id)
          ),
        })),
    [cioBrowseResults, pricingData, relatedProductsData]
  );

  const totalNumResults = cioBrowseResults?.pages?.[0]?.totalNumResults;

  const cioFacets = useMemo(() => {
    return cioBrowseResults?.pages?.[0]?.response?.facets;
  }, [cioBrowseResults]);

  const cioSortOptions = useMemo(() => {
    return cioBrowseResults?.pages?.[0]?.response?.sort_options;
  }, [cioBrowseResults]);

  return {
    products,
    fetchNextPage,
    hasNextPage,
    cioFacets,
    cioSortOptions,
    selectedUrlFilters,
    allCioFacets,
    allCioFacetNames,
    totalNumResults,
    isCioBrowseFetching,
    isRelatedProductsLoading,
  };
};

export default useCioBrowseAndGqlProducts;
